import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  CLIENT_STATUS,
  ClientStatusSchema,
  type AdminClientUpdateInput,
  type ClientStatus,
} from '@shared';
import { apiGet, apiPatch, ApiError } from '../../lib/api';
import { applyFieldErrors } from '../../lib/form-errors';

// Local Draft schema. Mirrors AdminClientUpdateInputSchema shape but every
// field is a plain string (empty string = "clear"), which matches what
// uncontrolled <input>s feed into RHF. We translate '' → null on the wire
// in diffPatch() so the server's `.nullable()` clauses apply correctly.
const EditDraftSchema = z.object({
  legal_name: z.string().min(1, 'Legal name is required').max(200),
  trading_name: z.string().max(200),
  registered_address: z.string().max(500),
  website: z.string().refine((s) => s === '' || /^https?:\/\//i.test(s), {
    message: 'Website must start with http:// or https://',
  }).refine((s) => s.length <= 200, { message: 'Website is too long' }),
  lead_staff_id: z.string(),
  status: ClientStatusSchema,
});

// Subset of the Client wire shape this drawer cares about. Kept narrow so the
// detail page can pass any super-set without ceremony.
interface EditableClient {
  id: string;
  name: string;
  trading_name?: string | null;
  registered_address?: string | null;
  website?: string | null;
  lead_staff_id?: string | null;
  status: ClientStatus;
}

interface LeadOption {
  id: string;
  name: string;
  role: string;
  initials: string;
}

interface ClientEditDrawerProps {
  client: EditableClient;
  onClose: () => void;
  onPatched: (next: EditableClient & Record<string, unknown>) => void;
}

const STATUS_LABEL: Record<ClientStatus, string> = {
  prospect: 'Prospect',
  active: 'Active',
  suspended: 'Suspended',
  deactivated: 'Deactivated',
};

// What the user can edit. Internal state only — we diff against `original`
// before PATCHing so audit detail stays tight.
type Draft = {
  legal_name: string;
  trading_name: string;
  registered_address: string;
  website: string;
  lead_staff_id: string;
  status: ClientStatus;
};

function draftFrom(c: EditableClient): Draft {
  return {
    legal_name: c.name ?? '',
    trading_name: c.trading_name ?? '',
    registered_address: c.registered_address ?? '',
    website: c.website ?? '',
    lead_staff_id: c.lead_staff_id ?? '',
    status: c.status,
  };
}

// Build a minimal PATCH body containing only the keys that actually changed.
// Empty-string optionals are sent as `null` to clear the value on the server
// (server schema accepts `.nullable()` on those fields).
function diffPatch(orig: Draft, next: Draft): AdminClientUpdateInput {
  const patch: AdminClientUpdateInput = {};
  if (orig.legal_name !== next.legal_name) patch.legal_name = next.legal_name;
  if (orig.trading_name !== next.trading_name) {
    patch.trading_name = next.trading_name === '' ? null : next.trading_name;
  }
  if (orig.registered_address !== next.registered_address) {
    patch.registered_address = next.registered_address === '' ? null : next.registered_address;
  }
  if (orig.website !== next.website) {
    patch.website = next.website === '' ? null : next.website;
  }
  if (orig.lead_staff_id !== next.lead_staff_id) {
    patch.lead_staff_id = next.lead_staff_id === '' ? null : next.lead_staff_id;
  }
  if (orig.status !== next.status) patch.status = next.status;
  return patch;
}

export function ClientEditDrawer({ client, onClose, onPatched }: ClientEditDrawerProps) {
  const original = useMemo(() => draftFrom(client), [client]);
  const [leads, setLeads] = useState<LeadOption[] | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [justSaved, setJustSaved] = useState(false);
  const firstFocusable = useRef<HTMLButtonElement>(null);
  const autoCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Use RHF for client-side zod validation parity with the server schema.
  // Mode = onChange so the dirty bit stays accurate as the operator types.
  const {
    register,
    handleSubmit,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<Draft>({
    resolver: zodResolver(EditDraftSchema),
    defaultValues: original,
  });

  // Watch the whole form so we can compute `dirty` and gate the Save button.
  const draft = watch();
  const patch = diffPatch(original, draft);
  const dirty = Object.keys(patch).length > 0;

  // Load active-lead list on mount. Soft-fail; dropdown just renders empty.
  useEffect(() => {
    apiGet<{ leads: LeadOption[] }>('/api/users/leads')
      .then((r) => setLeads(r.leads))
      .catch(() => setLeads([]));
  }, []);

  // Escape to close + body-scroll-lock — same TriageDrawer behaviour.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSubmitting) onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    firstFocusable.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
      if (autoCloseTimer.current) {
        clearTimeout(autoCloseTimer.current);
        autoCloseTimer.current = null;
      }
    };
  }, [onClose, isSubmitting]);

  const onSubmit = async () => {
    if (!dirty || justSaved) return; // belt-and-braces — button is disabled too.
    setSubmitError(null);
    try {
      const r = await apiPatch<{ client: EditableClient & Record<string, unknown> }>(
        `/api/clients/${client.id}`,
        patch,
      );
      onPatched(r.client);
      setJustSaved(true);
      // Mirror TriageDrawer: ~1.2 s acknowledgement, then close.
      autoCloseTimer.current = setTimeout(() => {
        autoCloseTimer.current = null;
        onClose();
      }, 1200);
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: { code?: string; message?: string; detail?: unknown } } | null;
        const code = body?.error?.code;
        if (err.status === 400 && code === 'validation_failed') {
          const handled = applyFieldErrors<Draft>(body?.error?.detail, setError);
          if (!handled) setSubmitError(err.serverMessage);
          return;
        }
        if (err.status === 422 && code === 'invalid_lead_staff') {
          setError('lead_staff_id', { type: 'server', message: 'Lead staff not found' });
          return;
        }
        if (err.status === 404) {
          setSubmitError('Client no longer exists. Refresh the list.');
          return;
        }
        setSubmitError(err.serverMessage);
      } else {
        setSubmitError('Failed to save. Please try again.');
      }
    }
  };

  return (
    <>
      <div
        className="drawer-backdrop"
        onClick={() => !isSubmitting && onClose()}
        aria-hidden="true"
      />
      <aside
        className="triage-drawer client-edit-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={`Edit ${client.name}`}
      >
        <header className="drawer-hd">
          <div>
            <div className="rid">{client.id}</div>
            <h2 className="drawer-title">Edit client</h2>
            <div className="drawer-meta">
              <span>{client.name}</span>
            </div>
          </div>
          <button
            ref={firstFocusable}
            type="button"
            className="drawer-close"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label="Close drawer"
          >
            ×
          </button>
        </header>

        <form className="drawer-body" onSubmit={handleSubmit(onSubmit)} noValidate>
          <section className="drawer-sec">
            <label className="drawer-sec-lbl" htmlFor="edit-legal-name">Legal name</label>
            <input
              id="edit-legal-name"
              type="text"
              className="drawer-input"
              autoComplete="organization"
              disabled={isSubmitting}
              aria-invalid={errors.legal_name ? 'true' : 'false'}
              {...register('legal_name')}
            />
            {errors.legal_name && (
              <div className="drawer-hint" style={{ color: 'var(--red)' }}>
                {errors.legal_name.message}
              </div>
            )}
          </section>

          <section className="drawer-sec">
            <label className="drawer-sec-lbl" htmlFor="edit-trading-name">Trading name</label>
            <input
              id="edit-trading-name"
              type="text"
              className="drawer-input"
              placeholder="Optional"
              disabled={isSubmitting}
              {...register('trading_name')}
            />
            {errors.trading_name && (
              <div className="drawer-hint" style={{ color: 'var(--red)' }}>
                {errors.trading_name.message}
              </div>
            )}
          </section>

          <section className="drawer-sec">
            <label className="drawer-sec-lbl" htmlFor="edit-registered-address">Registered address</label>
            <textarea
              id="edit-registered-address"
              rows={3}
              className="drawer-input"
              placeholder="Optional"
              disabled={isSubmitting}
              {...register('registered_address')}
            />
            {errors.registered_address && (
              <div className="drawer-hint" style={{ color: 'var(--red)' }}>
                {errors.registered_address.message}
              </div>
            )}
          </section>

          <section className="drawer-sec">
            <label className="drawer-sec-lbl" htmlFor="edit-website">Website</label>
            <input
              id="edit-website"
              type="url"
              className="drawer-input"
              placeholder="https://"
              autoComplete="url"
              disabled={isSubmitting}
              aria-invalid={errors.website ? 'true' : 'false'}
              {...register('website')}
            />
            {errors.website && (
              <div className="drawer-hint" style={{ color: 'var(--red)' }}>
                {errors.website.message}
              </div>
            )}
          </section>

          <section className="drawer-sec">
            <label className="drawer-sec-lbl" htmlFor="edit-lead-staff">Engagement lead</label>
            <select
              id="edit-lead-staff"
              className="drawer-select"
              disabled={isSubmitting || leads === null}
              aria-invalid={errors.lead_staff_id ? 'true' : 'false'}
              {...register('lead_staff_id')}
            >
              <option value="">Unassigned</option>
              {(leads ?? []).map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name} · {l.role}
                </option>
              ))}
            </select>
            {leads === null && <div className="drawer-hint">Loading leads…</div>}
            {errors.lead_staff_id && (
              <div className="drawer-hint" style={{ color: 'var(--red)' }}>
                {errors.lead_staff_id.message}
              </div>
            )}
          </section>

          <section className="drawer-sec">
            <label className="drawer-sec-lbl" htmlFor="edit-status">Status</label>
            <select
              id="edit-status"
              className="drawer-select"
              disabled={isSubmitting}
              {...register('status')}
            >
              {CLIENT_STATUS.map((s) => (
                <option key={s} value={s}>{STATUS_LABEL[s]}</option>
              ))}
            </select>
            {errors.status && (
              <div className="drawer-hint" style={{ color: 'var(--red)' }}>
                {errors.status.message}
              </div>
            )}
          </section>

          <footer className="drawer-ft">
            {submitError && <div className="drawer-error">{submitError}</div>}
            {justSaved && (
              <div
                role="status"
                aria-live="polite"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '10px 14px',
                  marginBottom: 10,
                  borderRadius: 'var(--r-sm)',
                  background: 'color-mix(in oklab, var(--green, #1f8a4c) 12%, transparent)',
                  color: 'var(--green, #1f8a4c)',
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                <span aria-hidden="true">✓</span>
                <span>Changes saved.</span>
              </div>
            )}
            <div className="drawer-actions">
              <button
                type="button"
                className="btn ghost"
                onClick={onClose}
                disabled={isSubmitting}
              >
                {justSaved ? 'Close now' : 'Cancel'}
              </button>
              <button
                type="submit"
                className="btn accent"
                disabled={!dirty || isSubmitting || justSaved}
              >
                {isSubmitting ? 'Saving…' : justSaved ? 'Saved ✓' : dirty ? 'Save changes' : 'No changes'}
              </button>
            </div>
          </footer>
        </form>
      </aside>
    </>
  );
}
